import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type SyntheticEvent,
  type TransitionEvent,
} from 'react';
import { Overlay } from '../../atoms/Overlay/Overlay';
import styles from './Dialog.module.css';

/**
 * The modal layer `Modal` and `Drawer` share: a full-viewport `<dialog>` holding
 * an `Overlay` and one panel, which is exactly how Figma builds both — a
 * 1440×900 frame with an `Overlay` instance behind a card. Where the panel sits
 * is the caller's `className`; this owns the behaviour.
 *
 * None of the behaviour is in Figma, which draws the open state only:
 *
 * - **Native `showModal()`.** The page behind becomes inert, focus moves into
 *   the dialog and is held there, and the layer renders in the top layer, so no
 *   ancestor's `overflow` or `z-index` can clip or bury it.
 * - **Escape** closes, through the dialog's own `cancel` event. It is routed to
 *   `onClose` rather than left to close the element, so `open` stays the single
 *   source of truth.
 * - **A click on the overlay** closes — the pointer equivalent of Escape.
 * - **Focus returns** to whatever held it before opening, once the exit
 *   transition has finished and the page is no longer inert.
 * - **The page does not scroll** behind an open dialog.
 * - **Enter and exit are animated** on the motion tokens. The layer outlives
 *   `open` by one transition, the same way `Popover`'s panel does. The overlay
 *   and panel fade, not the layer, so the overlay's backdrop blur fades in with
 *   its tint rather than snapping in at the end, and the entrance waits one
 *   frame for the content to mount — see `entered`.
 *
 * Not exported from the package: Figma has no dialog component of its own, only
 * `Modal` and `Drawer`, and those are the two things a consumer should reach for.
 */
export type DialogProps = {
  open: boolean;
  onClose: () => void;
  /** Id of the element that names the dialog — the header title. */
  labelledBy?: string;
  /** Id of the element that describes it — the header's supporting text. */
  describedBy?: string;
  /** Positions the panel inside the viewport-sized layer. */
  className?: string;
  /** The panel. */
  children: ReactNode;
};

export function Dialog({
  open,
  onClose,
  labelledBy,
  describedBy,
  className,
  children,
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  /*
   * The entrance starts one frame AFTER the layer mounts, not with it.
   * Mounting a dialog's content is the most expensive frame it will ever have —
   * `PostBreakdownDrawer`'s charts and tables blocked the main thread for 100ms+
   * — and a transition started in that same commit spends its fastest phase
   * behind the block: the drawer appeared already 70px in and then crept the
   * last stretch. Flipping `data-open` on the next frame lets the work finish
   * first, so the whole travel is visible.
   *
   * It is ANDed with `open` rather than used alone, so closing still drops the
   * attribute in the same render the prop changes and the exit never waits.
   */
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [open]);

  /* See `Popover` — adjusting state during render avoids a one-frame flicker. */
  const [exiting, setExiting] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    setExiting(!open);
  }

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (!dialog.open) {
      returnFocusRef.current = document.activeElement as HTMLElement | null;
      dialog.showModal();
    }

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => {
      root.style.overflow = previousOverflow;
    };
  }, [open]);

  /*
   * Once the layer is fully gone the page is no longer inert and focus can go
   * back. Doing it when `open` flips would be too early — the dialog is still
   * modal during the exit, so the trigger cannot take focus yet.
   */
  useEffect(() => {
    if (open || exiting) return;
    const target = returnFocusRef.current;
    returnFocusRef.current = null;
    target?.focus({ preventScroll: true });
  }, [open, exiting]);

  const onCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault();
    onClose();
  };

  /*
   * The platform can close the element without a cancellable `cancel` — Chrome
   * does on a second Escape with no user activation in between. When that
   * happens the element is already hidden, so there is no exit to wait for.
   */
  const onNativeClose = () => {
    setExiting(false);
    if (openRef.current) onClose();
  };

  /*
   * The fade runs on the layer's children, not the layer (see the stylesheet),
   * and they all share one timing — so the first child to finish is the signal.
   * Anything deeper that happens to transition opacity is ignored.
   */
  /*
   * The exit ends on a child's `transitionend` — unless there is no exit to run.
   * Closing before the entrance has started (see `entered`) leaves the children
   * already at their closed styles: nothing transitions, no event arrives, and
   * the layer would stay mounted for good. One frame after closing, a layer with
   * nothing running is done. Same guard as `HelpIcon`'s.
   */
  useEffect(() => {
    if (!exiting) return;
    const frame = requestAnimationFrame(() => {
      const children = [...(dialogRef.current?.children ?? [])];
      if (children.every((child) => child.getAnimations().length === 0)) setExiting(false);
    });
    return () => cancelAnimationFrame(frame);
  }, [exiting]);

  const onTransitionEnd = (event: TransitionEvent<HTMLDialogElement>) => {
    const target = event.target as Element;
    if (!open && target.parentElement === dialogRef.current && event.propertyName === 'opacity') {
      setExiting(false);
    }
  };

  if (!open && !exiting) return null;

  return (
    <dialog
      ref={dialogRef}
      className={[styles.dialog, className].filter(Boolean).join(' ')}
      data-open={(open && entered) || undefined}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      inert={!open}
      onCancel={onCancel}
      onClose={onNativeClose}
      onTransitionEnd={onTransitionEnd}
    >
      <Overlay onClick={onClose} />
      {children}
    </dialog>
  );
}
